import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { sb } from '../supabase';
import { getFactoryClaimPreview, claimFactoryBySlug } from '../lib/factoryClaim';
import { DISPLAY_CURRENCIES } from '../lib/displayCurrency';
import { displayCategoryForCode } from '../lib/factoryCategories';
import { COUNTRY_CODES } from '../lib/countryCodes';
import { CLAIM_STRINGS } from './claimStrings';
import { logClaimEvent, installClaimErrorCapture } from '../lib/claimFunnel';
import { sanitizeNextPath } from '../lib/authRedirects';
import { waLink } from '../lib/maabarContact';
import { fetchLatestOwnerThreadForFactory } from '../lib/factoryChat';

const nf = (v) => (v && v !== 'not_found' ? String(v).trim() : '');

// Claim intent — set just before an email signUp, matched on return. localStorage,
// not sessionStorage: a confirmation link opened from an email client is a fresh
// browsing context, which is precisely the case this resume exists for. Short TTL so
// a forgotten intent cannot auto-claim later.
const CLAIM_INTENT_KEY = 'maabar.claimIntent';
const CLAIM_INTENT_TTL_MS = 60 * 60 * 1000;
const setClaimIntent = (slug) => {
  try { localStorage.setItem(CLAIM_INTENT_KEY, JSON.stringify({ slug, ts: Date.now() })); } catch { /* private mode */ }
};
const clearClaimIntent = () => { try { localStorage.removeItem(CLAIM_INTENT_KEY); } catch { /* private mode */ } };
const claimIntentMatches = (slug) => {
  try {
    const raw = JSON.parse(localStorage.getItem(CLAIM_INTENT_KEY) || 'null');
    return !!raw && raw.slug === slug && Date.now() - raw.ts < CLAIM_INTENT_TTL_MS;
  } catch { return false; }   // unreadable storage → fall back to the manual button
};

// WhatsApp code delivery runs through the Supabase Send SMS Hook → Widers.
//
// LIVE since 2026-09-08. The whole path is deployed and was confirmed end to
// end: signInWithOtp({phone}) → GoTrue → send-sms-hook → whatsapp-send →
// providers/widers.ts → Widers → WhatsApp, with auth_code's copy-code button
// verified rendering on a real handset in BOTH en_US and zh_CN.
//
// To close it again, set this false. The WhatsApp method stays VISIBLE either
// way — factories must see it — but cannot be submitted, and the card explains
// why in their language (t.waNotReady). This is the fastest lever; the
// server-side equivalent is WA_OTP_ENABLED in the Edge secrets, which stops
// delivery with no deploy at all.
//
// It must never call signInWithOtp while false: GoTrue still generates and
// routes a code regardless, and with the hook disabled that means an SMS to a
// +86 number that vanishes — the silent failure this design exists to avoid.
const WHATSAPP_OTP_LIVE = true;

// The hosted project issues 8-digit codes while supabase/config.toml still reads
// otp_length = 6 — the dashboard is authoritative, not the local file. Pinning
// the input to six made the real code impossible to type: maxLength truncated it
// and the guard rejected what was left. So no length is assumed anywhere, and the
// copy states none either, or it drifts again the next time the setting moves.
// Supabase rejects a wrong code regardless, so a loose client bound costs nothing.
const MIN_CODE = 4;
const MAX_CODE = 10;

// ── The WhatsApp number ──────────────────────────────────────────────────────
// The invite reached this factory on WhatsApp, so we already know a number, and
// get_factory_claim_preview hands it back (only while the factory is unclaimed).
// The page pre-fills it MASKED and lets them change it: signing in from a phone
// other than the one we messaged is expected — a shared company handset, a sales
// manager's own line — so nothing here compares what is typed to what we knew.
//
// Same shape as factory_directory_wa_phone_e164_chk and the Widers send path
// (20260904223654), so a number this page accepts is one the sender can use.
// Named errors from claim_factory_by_slug get named copy; anything else keeps
// its own message, which is what this page did for everything until now.
//
// Raw provider strings must not reach this page: the reader is a Chinese factory
// owner mid-signup, and 'CLAIM_IDENTITY_MISMATCH' on screen at that moment is
// worse than a refusal — it is a refusal with no next step. The two codes are
// matched on substring because plpgsql prefixes its own context onto the
// message, and on lower case because nothing guarantees the casing survives the
// client library.
function claimErrorText(e, t) {
  const m = String(e?.message || e || '').toLowerCase();
  if (m.includes('claim_identity_mismatch')) return t.errIdentityMismatch;
  if (m.includes('claim_no_contact_on_file')) return t.errNoContactOnFile;
  return e?.message || 'claim failed';
}

const E164 = /^\+[1-9][0-9]{7,14}$/;

// Longest match first: dial codes are not prefix-free (+1 vs +1876, +7, +39,
// +44 …), so a naive scan would strip +1 off a Jamaican number.
const DIALS = [...new Set(COUNTRY_CODES.map((c) => c.dial))].sort((a, b) => b.length - a.length);
const dialOf = (e164) => DIALS.find((d) => e164.startsWith(d)) || '';
const countryByIso = (iso) => COUNTRY_CODES.find((c) => c.iso === iso);

// Flag emoji = the ISO pair as regional indicators. Derived, never stored —
// countryCodes.js keeps data only, deliberately.
const flagOf = (iso) => String.fromCodePoint(...[...iso].map((c) => 0x1F1E6 + c.charCodeAt(0) - 65));

// The factory's own country picks the default dial code; China when it is absent
// or written in a form this list does not carry.
const isoForCountry = (name) => {
  const raw = String(name || '').trim();
  const lower = raw.toLowerCase();
  const hit = raw && COUNTRY_CODES.find((c) => c.name_en.toLowerCase() === lower || c.name_zh === raw);
  return hit ? hit.iso : 'CN';
};

// "+8613800138000" → "+86 138 **** 8000". Dial code, the first three national
// digits and the last four stay; the middle is hidden digit-for-digit so the
// length still reads true.
const maskPhone = (e164) => {
  const v = String(e164 || '').trim();
  if (!/^\+\d+$/.test(v)) return v;
  const dial = dialOf(v);
  const nat = v.slice(dial.length);
  if (nat.length <= 4) return `${dial} ${nat}`.trim();
  const head = nat.slice(0, Math.min(3, nat.length - 4));
  const tail = nat.slice(-4);
  return `${dial} ${head} ${'*'.repeat(nat.length - head.length - 4)} ${tail}`.replace(/\s+/g, ' ').trim();
};

// Join a dial code to a national number typed with whatever separators came to
// hand, and drop the trunk 0 ("+86 0138…" is the classic paste). Not a validator
// — E164 above is the only gate.
const toE164 = (dial, national) => {
  const nat = String(national || '').replace(/\D/g, '').replace(/^0+/, '');
  return nat ? `${dial}${nat}` : '';
};

// The official WhatsApp mark — the SAME path already shipping in
// components/SupportWhatsapp.jsx:66, copied verbatim rather than redrawn, and
// no new dependency (this project has no icon package). Brand green is fixed,
// not currentColor: it is a trademark, not a themeable glyph.
function WhatsAppIcon({ size = 20 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="#25D366" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488" />
    </svg>
  );
}

// A plain envelope, drawn here because it is a generic glyph and not a mark.
// currentColor so it inherits the button's ink.
function EnvelopeIcon({ size = 20 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" />
      <path d="M3 7.5l9 6 9-6" />
    </svg>
  );
}

export default function ClaimFactory({ onLangChange }) {
  const { slug: rawSlug } = useParams();
  // Tolerate junk appended to the slug in the wild — a trailing '|' pasted from a
  // copied table cell, stray whitespace, an accidental '/'. Keep valid slug chars
  // only, so /claim/W9QPZW5| still resolves to W9QPZW5 instead of a dead "not found".
  const slug = (rawSlug || '').replace(/[^A-Za-z0-9._-]/g, '');
  const nav = useNavigate();
  // ?next= — where the visitor was actually heading when this page interrupted
  // them (a WhatsApp entry route hands us its own path so it can re-resolve once
  // they are authenticated). It needs no persistence: the code is entered inline
  // on this same page, so the query string survives the whole OTP flow untouched.
  // Sanitised through the same helper Login and AuthCallback use — internal
  // absolute paths only, no open redirect (authRedirects.js:26).
  const [searchParams] = useSearchParams();
  const rawNext = searchParams.get('next');
  const nextPath = rawNext ? sanitizeNextPath(rawNext) : '';
  const [lang, setLang] = useState('zh');   // supplier-facing → Chinese default
  const [pre, setPre] = useState(undefined);   // undefined=loading, null=notfound
  const [user, setUser] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);   // session resolved (a user, or definitively none)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [authMode, setAuthMode] = useState('none');   // none | whatsapp | email
  const [email, setEmail] = useState('');
  // Phone: `phoneIso` + `phoneNat` are the EDIT state only. While the suggested
  // number stands untouched it is itself the value that gets sent, so there is no
  // second copy of it to keep in sync.
  const [phoneEdit, setPhoneEdit] = useState(false);
  const [phoneIso, setPhoneIso] = useState('CN');
  const [phoneNat, setPhoneNat] = useState('');
  const [ccOpen, setCcOpen] = useState(false);
  const [ccQuery, setCcQuery] = useState('');
  const [pass, setPass] = useState('');
  const [msg, setMsg] = useState('');
  // Inline code step. `pass` above is captured in the SAME card as the identifier
  // (step one) and held in component state only — never sent with the code
  // request, never persisted — until verifyOtp returns a session.
  const [otpSent, setOtpSent] = useState(false);
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);   // resend countdown (UX only)
  // Post-claim pricing step
  const [step, setStep] = useState('claim');   // 'claim' | 'price'
  const [myProducts, setMyProducts] = useState([]);
  const [uPrice, setUPrice] = useState('');
  const [uCurrency, setUCurrency] = useState('USD');
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const t = CLAIM_STRINGS[lang] || CLAIM_STRINGS.zh;

  // The toggle is the owner's explicit language choice — persist it the way the rest
  // of the app does (localStorage + profiles.lang, via handleSetLang) instead of
  // leaving it as page-local state that dies the moment they reach the dashboard.
  // The page keeps its own zh default for its own rendering: it has no Arabic strings
  // and is hardcoded dir="ltr".
  const pickLang = (l) => { setLang(l); onLangChange?.(l); };

  // Resolve the session FIRST. get_factory_claim_preview computes is_owner as
  // `linked_supplier_id = auth.uid()`, so a preview sent before supabase-js has
  // restored the session from storage comes back is_owner=false for a genuine
  // owner — and the page then tells them their own factory belongs to another
  // account. It depended on network timing, so it never reproduced reliably.
  useEffect(() => {
    logClaimEvent(slug, 'link_opened');
    const removeErrorCapture = installClaimErrorCapture(slug, 'claim');
    let alive = true;
    sb.auth.getSession()
      .then(({ data }) => { if (alive) setUser(data?.session?.user || null); })
      .catch(() => { /* unreadable session → treat as signed out */ })
      .finally(() => { if (alive) setSessionReady(true); });
    return () => { alive = false; removeErrorCapture(); };
  }, [slug]);

  // Funnel events describe the FIRST preview of this link. A refetch (after a
  // sign-in on this page) must not double-count them.
  const previewLoggedRef = useRef(false);
  // Refetch whenever the identity changes: a visitor who signs in here may turn
  // out to be the owner, and is_owner is only ever computed server-side.
  const userId = user?.id || null;
  useEffect(() => {
    if (!sessionReady) return undefined;   // never preview on an unresolved session
    let alive = true;
    getFactoryClaimPreview(slug)
      .then((d) => {
        if (!alive) return;
        setPre(d);
        if (previewLoggedRef.current) return;
        previewLoggedRef.current = true;
        if (!d) { logClaimEvent(slug, 'preview_not_found'); return; }
        if (d.already_claimed && !d.is_owner) logClaimEvent(slug, 'already_claimed_by_other');
        else logClaimEvent(slug, 'preview_seen', {
          products: Number(d.product_count) || 0,
          verified: !!d.is_verified,
          is_owner: !!d.is_owner,
        });
      })
      .catch((e) => {
        if (!alive) return;
        setPre(null);
        if (previewLoggedRef.current) return;
        previewLoggedRef.current = true;
        logClaimEvent(slug, 'preview_error', { message: String(e?.message || e).slice(0, 200) });
      });
    return () => { alive = false; };
  }, [slug, userId, sessionReady]);

  // Seed the phone field from the preview ONCE. The preview refetches whenever the
  // identity changes, and a refetch must never discard a number half-typed.
  const phoneSeededRef = useRef(false);
  useEffect(() => {
    if (!pre || phoneSeededRef.current) return;
    phoneSeededRef.current = true;
    setPhoneIso(isoForCountry(pre.country));
    // Nothing usable to suggest → open straight into the editor.
    setPhoneEdit(!E164.test(String(pre.wa_phone_e164 || '')));
  }, [pre]);

  // ── Auth: inline code entry, one mechanism and two delivery channels ──
  // Supabase Auth is the single source of truth for the code — GoTrue generates,
  // hashes, expires and attempt-counts it. Only DELIVERY differs: email through
  // the Send Email Hook (supabase/functions/send-email/index.ts:1145) → Resend,
  // WhatsApp through the Send SMS Hook → Widers. verifyOtp type stays 'sms' for
  // the phone channel — the hook replaces delivery, not the verification type.
  const isWa = authMode === 'whatsapp';
  const waBlocked = isWa && !WHATSAPP_OTP_LIVE;
  // Null once the factory is claimed — the RPC gates the column, not the page.
  const waSuggested = E164.test(String(pre?.wa_phone_e164 || '')) ? String(pre.wa_phone_e164) : '';
  const phoneDial = (countryByIso(phoneIso) || countryByIso('CN')).dial;
  // Send always uses the FULL value; the mask is display only.
  const waPhone = (waSuggested && !phoneEdit) ? waSuggested : toE164(phoneDial, phoneNat);
  const dest = () => (isWa ? waPhone : email.trim());
  const claimedByAnother = () => !!(pre?.already_claimed && !pre?.is_owner);

  // Resend countdown. UX only: the anon key is public, so anyone can POST
  // /auth/v1/otp directly. The real limits are GoTrue's (Auth → Rate Limits).
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((n) => Math.max(0, n - 1)), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  // 'none' is the back button, not a method choice — keep it out of the funnel.
  const pickMethod = (m) => { setErr(''); setMsg(''); setOtpSent(false); setCode(''); if (m !== 'none') logClaimEvent(slug, 'signup_method', { method: m }); setAuthMode(m); };
  // Step one: identifier + password in the SAME card. Only the identifier is sent.
  const sendCode = async () => {
    setErr(''); setMsg('');
    // Hard stop, not just a disabled button: never let a WhatsApp request fall
    // through to Twilio SMS while the Widers adapter is not deployed.
    if (waBlocked) { setErr(t.waNotReady); return; }
    const to = dest();
    if (isWa) { if (!E164.test(to)) { setErr(t.errPhone); return; } }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) { setErr(t.errEmail); return; }
    if (pass.length < 6) { setErr(t.passPh); return; }
    setBusy(true);
    logClaimEvent(slug, 'otp_requested', { method: authMode });
    try {
      const company = nf(pre?.company_name_latin) || nf(pre?.company_name) || '';
      setClaimIntent(slug);
      // `role` is deliberately NOT sent: claim_factory_by_slug is the authoritative
      // promoter, so abandoning here leaves a plain profile with no factory link and
      // the factory stays claimable. shouldCreateUser is false once the factory
      // belongs to someone else — a new account could never claim it, so creating
      // one would only orphan it.
      // claim_slug marks the account as born on a claim page: the admin
      // "new trader" email skips it (20261012025554), since for one moment the
      // profile is a plain one before the claim promotes it. Only read on
      // CREATION — options.data is ignored for an existing user.
      const options = { shouldCreateUser: !claimedByAnother(), data: { company_name: company, lang, claim_slug: slug } };
      const { error } = await sb.auth.signInWithOtp(isWa ? { phone: to, options } : { email: to, options });
      if (error) {
        clearClaimIntent();
        logClaimEvent(slug, 'otp_send_failed', { method: authMode, message: String(error.message || '').slice(0, 200) });
        // Never render the raw provider string on a Chinese page.
        setErr(/not allowed|otp_disabled|signups not allowed/i.test(error.message || '') ? t.errNoAccount : t.errSend);
        setBusy(false); return;
      }
      logClaimEvent(slug, 'otp_sent', { method: authMode });
      setOtpSent(true); setCode(''); setCooldown(60);
    } catch (e) {
      clearClaimIntent();
      logClaimEvent(slug, 'otp_send_failed', { method: authMode, phase: 'exception', message: String(e?.message || e).slice(0, 200) });
      setErr(t.errSend);
    }
    setBusy(false);
  };

  // Step two: the code. On success the password is saved immediately.
  const verifyCode = async () => {
    const token = code.trim();
    if (token.length < MIN_CODE) { setErr(t.errCode); return; }
    setErr(''); setBusy(true);
    const to = dest();
    logClaimEvent(slug, 'otp_verify_attempt', { method: authMode });
    try {
      const { data, error } = await sb.auth.verifyOtp(
        isWa ? { phone: to, token, type: 'sms' } : { email: to, token, type: 'email' },
      );
      if (error || !data?.session) {
        logClaimEvent(slug, 'otp_verify_failed', { method: authMode, message: String(error?.message || '').slice(0, 200) });
        setErr(t.errCode); setBusy(false); return;
      }
      logClaimEvent(slug, 'otp_verified', { method: authMode });
      // The moment there is a session. Non-fatal: they are signed in either way and
      // can set a password from the dashboard, so a failure here must not block the
      // claim they came for.
      try {
        // data.lang too: signInWithOtp's options.data is ignored for an existing
        // account, so this is the only moment we can correct the stored language —
        // it makes the NEXT email right without leaning on the hook's clamp.
        const { error: pwErr } = await sb.auth.updateUser({ password: pass, data: { lang } });
        logClaimEvent(slug, pwErr ? 'password_set_failed' : 'password_set', pwErr ? { message: String(pwErr.message || '').slice(0, 200) } : undefined);
      } catch (e) { logClaimEvent(slug, 'password_set_failed', { phase: 'exception', message: String(e?.message || e).slice(0, 200) }); }
      setUser(data.session.user);
      // Already someone else's: do NOT attempt the claim — the RPC would throw. The
      // preview refetch and owner auto-route decide what they see next.
      if (claimedByAnother()) { setBusy(false); return; }
      await doClaim();
    } catch (e) {
      logClaimEvent(slug, 'otp_verify_failed', { method: authMode, phase: 'exception', message: String(e?.message || e).slice(0, 200) });
      setErr(t.errCode); setBusy(false);
    }
  };
  const doClaim = async () => {
    setErr(''); setBusy(true);
    logClaimEvent(slug, 'claim_attempted');
    try {
      // Server-side backstop: claim_factory_by_slug binds linked_supplier_id, seeds the
      // products and sets the supplier role itself — authoritative even when the signup
      // metadata already created the account as a supplier, and the ONLY mechanism on
      // the Google path where OAuth controls the metadata.
      const res = await claimFactoryBySlug(slug);
      if (!res?.factory_id) throw new Error(t.notfound);
      logClaimEvent(slug, 'claim_succeeded', { factoryId: res.factory_id });
      clearClaimIntent();
      // A Chinese owner usually never touches the toggle — zh is already the default —
      // so persisting only on toggle would miss the common case. Completing the whole
      // claim in this language is itself an explicit signal, so persist it now.
      onLangChange?.(lang);
      await loadMyProducts();
      setStep('price');
      logClaimEvent(slug, 'pricing_shown');
      setBusy(false);
    } catch (e) {
      logClaimEvent(slug, 'claim_failed', { message: String(e?.message || e).slice(0, 200) });
      setErr(claimErrorText(e, t));
      setBusy(false);
    }
  };

  const loadMyProducts = async () => {
    const { data: { user: u } } = await sb.auth.getUser();
    if (!u) return;
    const { data: prods } = await sb.from('products')
      .select('id, name_en, name_zh, name_ar, currency, moq, image_url')
      .eq('supplier_id', u.id).eq('is_draft', false)
      .order('created_at', { ascending: true });
    const list = prods || [];
    const ids = list.map((p) => p.id);
    let priced = new Set();
    if (ids.length) {
      const { data: tiers } = await sb.from('product_pricing_tiers')
        .select('product_id').in('product_id', ids).is('variant_id', null);
      priced = new Set((tiers || []).map((r) => r.product_id));
    }
    setMyProducts(list.map((p) => ({ ...p, priced: priced.has(p.id) })));
  };

  const resumedRef = useRef(false);
  // With email confirmation on, signUp returns no session and doClaim never ran. On
  // return (session + our intent for THIS slug), finish the claim automatically.
  // Deliberately NOT "auto-claim any signed-in visitor": the claim is
  // first-claimant-wins, so that would let a trader who merely opens a shared link
  // take ownership of the factory.
  useEffect(() => {
    if (resumedRef.current || !user || !pre) return;
    if (pre.already_claimed || pre.is_owner) return;
    if (!claimIntentMatches(slug)) return;
    resumedRef.current = true;
    logClaimEvent(slug, 'verify_returned', { method: 'email' });
    doClaim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pre, slug]);

  // Owner auto-route. Once the session is resolved and the preview says this
  // account already owns the factory, the claim UI is never shown: resolve where
  // they should actually be and go there — the newest conversation on this
  // factory, on the unified `messages` stack. claim_factory_by_slug is no longer
  // consulted here: it was only ever used for its thread_id, which pointed at the
  // retired factory_threads stack, and it is a no-op for an existing owner.
  const ownerRoutedRef = useRef(false);
  useEffect(() => {
    if (ownerRoutedRef.current || !pre?.is_owner) return;
    if (step === 'price') return;   // a just-completed claim owns the screen
    // is_owner is computed server-side from auth.uid(), so a session exists — but
    // wait for the id to reach state before resolving, since the lookup needs it.
    if (!userId) return;
    ownerRoutedRef.current = true;
    (async () => {
      logClaimEvent(slug, 'owner_redirect');
      // An explicit ?next= wins: the caller already knows where this owner should
      // land and will re-resolve it themselves, so there is nothing to look up.
      let dest = nextPath;
      if (!dest) {
        try {
          const th = await fetchLatestOwnerThreadForFactory(userId, pre.factory_id);
          if (th) dest = `/chat/f/${th.factory_id}/${th.trader_id}`;
        } catch (e) {
          // Never strand the owner on the claim page — the dashboard is always valid.
          logClaimEvent(slug, 'owner_redirect_error', { message: String(e?.message || e).slice(0, 200) });
        }
        if (!dest) dest = '/dashboard';
      }
      window.location.assign(dest);
    })();
  }, [pre, slug, step, nextPath, userId]);

  // App.js caches the profile at signup, BEFORE the claim upgrades the role, and it
  // deliberately ignores TOKEN_REFRESHED and same-user SIGNED_IN (App.js:529,547) to
  // avoid remounting the Router tree — so refreshSession() would NOT re-read the
  // profile and nav('/dashboard') would route on the stale 'buyer'. A full load does.
  // Honours ?next= when one was supplied, so a claim that began from an entry
  // route ends where that route intended rather than on a generic dashboard.
  const goAfterClaim = () => { logClaimEvent(slug, 'dashboard_go'); window.location.assign(nextPath || '/dashboard'); };

  const applyUniform = async () => {
    const price = parseFloat(uPrice);
    if (!Number.isFinite(price) || price <= 0) { setErr(t.priceErr); return; }
    setErr(''); setApplying(true);
    const targets = myProducts.filter((p) => !p.priced);
    const ids = targets.map((p) => p.id);
    try {
      if (ids.length) {
        await sb.from('products').update({ currency: uCurrency }).in('id', ids);
        await sb.from('product_pricing_tiers').delete().in('product_id', ids).is('variant_id', null);
        const rows = targets.map((p) => ({ product_id: p.id, variant_id: null, qty_from: p.moq || 1, qty_to: null, unit_price: price }));
        await sb.from('product_pricing_tiers').insert(rows);
      }
      setMyProducts((prev) => prev.map((p) => (p.priced ? p : { ...p, priced: true, currency: uCurrency, unit_price: price })));
      setApplied(true);
      logClaimEvent(slug, 'pricing_applied', { count: ids.length });
    } catch (e) { setErr(e.message || 'failed'); }
    setApplying(false);
  };

  // ── shared shell ────────────────────────────────────────────────────────
  const shell = (children) => (
    <div className="cf-root" dir="ltr">
      <style>{CSS}</style>
      <div className="cf-top">
        <div className="cf-brand">
          <span className="cf-mark">M</span>
          <div>
            <div className="cf-wordmark">MAABAR <span>مَعبر</span></div>
            <div className="cf-brandsub">{t.brandSub}</div>
          </div>
        </div>
        <div className="cf-langs">
          <button className={lang === 'zh' ? 'on' : ''} onClick={() => pickLang('zh')}>中文</button>
          <button className={lang === 'en' ? 'on' : ''} onClick={() => pickLang('en')}>EN</button>
        </div>
      </div>
      {children}
      <p className="cf-foot">MAABAR ｜ معبر — {t.footer} · © 2026</p>
    </div>
  );

  if (pre === undefined) return shell(<p className="cf-center">{t.loading}</p>);
  if (pre === null) return shell(<p className="cf-center">{t.notfound}</p>);
  // Owner: the effect above is already resolving their real destination, so the
  // claim UI is never rendered for them. 'price' still wins — a just-completed
  // claim owns the screen.
  if (pre.is_owner && step !== 'price') return shell(<p className="cf-center">{t.loading}</p>);

  const name = nf(pre.company_name_latin) || nf(pre.company_name) || 'Factory';
  const claimedOther = pre.already_claimed && !pre.is_owner;
  const catObj = displayCategoryForCode(pre.category);
  const catLabel = catObj ? (catObj.label[lang] || catObj.label.en) : nf(pre.category);
  const preview = Array.isArray(pre.products_preview) ? pre.products_preview : [];
  const prodName = (p) => (lang === 'zh' ? (nf(p.name_zh) || nf(p.name_en)) : (nf(p.name_en) || nf(p.name_zh))) || nf(p.name_ar) || '—';
  const total = Number(pre.product_count) || 0;
  const priced = Number(pre.priced_count) || 0;
  const unpriced = Math.max(0, total - priced);
  const pct = total > 0 ? Math.round((priced / total) * 100) : 0;
  const goSignin = () => {
    if (user) { goAfterClaim(); return; }
    setAuthMode('none'); setErr('');
    document.getElementById('cf-signin')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // ── Post-claim: price your products (unchanged) ──────────────────────────
  if (step === 'price') {
    const unp = myProducts.filter((p) => !p.priced).length;
    const pname = (p) => (lang === 'zh' ? (p.name_zh || p.name_en) : (p.name_en || p.name_zh)) || p.name_ar || '—';
    return shell(
      <>
        <div className="cf-hero-head">
          <h1 className="cf-welcome">🎉 {t.claimedOk}</h1>
          <h2 className="cf-title" style={{ fontSize: 26 }}>{t.priceTitle}</h2>
        </div>
        <p className="cf-subtitle" style={{ textAlign: 'center' }}>{t.priceSub}</p>
        <div className="cf-price-card">
          {unp > 0 ? (
            <>
              <div className="cf-price-hint">💰 {t.unpricedCount(unp)}</div>
              <div className="cf-price-row">
                <input className="cf-input" type="number" min="0" inputMode="decimal" placeholder={t.pricePh} value={uPrice} onChange={(e) => setUPrice(e.target.value)} dir="ltr" />
                <select className="cf-input cf-cur" value={uCurrency} onChange={(e) => setUCurrency(e.target.value)}>
                  {DISPLAY_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <button className="cf-btn cf-primary" onClick={applyUniform} disabled={applying || !uPrice}>{applying ? t.working : t.applyBtn}</button>
              {applied && <p className="cf-msg">{t.appliedOk}</p>}
            </>
          ) : (
            <div className="cf-price-hint">✅ {t.allPriced}</div>
          )}
        </div>
        <div className="cf-prodlist">
          {myProducts.map((p) => (
            <div className="cf-prod" key={p.id}>
              <div className="cf-prod-img" style={p.image_url ? { backgroundImage: `url(${p.image_url})` } : {}} />
              <div className="cf-prod-body">
                <div className="cf-prod-name">{pname(p)}</div>
                <div className={`cf-prod-price ${p.priced ? 'on' : 'off'}`}>
                  {p.priced ? (p.unit_price ? `${p.unit_price} ${p.currency || uCurrency} ${t.perUnit}` : t.pricedTag) : t.noPrice}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 18, textAlign: 'center' }}>
          <button className="cf-btn cf-primary" onClick={goAfterClaim}>{t.toDashboard}</button>
        </div>
        {err && <p className="cf-err">{err}</p>}
      </>
    );
  }

  // ── The phone field ───────────────────────────────────────────────────────
  // A native <select> would be less code, but 243 countries need a search box,
  // and searching by dial code matters as much as by name — an owner knows "86"
  // long before they find their country in an English list.
  const ccQ = ccQuery.trim();
  const ccDigits = ccQ.replace(/\D/g, '');
  const ccFiltered = !ccQ ? COUNTRY_CODES : COUNTRY_CODES.filter((c) => (
    c.name_en.toLowerCase().includes(ccQ.toLowerCase())
    || c.name_zh.includes(ccQ)
    || (!!ccDigits && c.dial.slice(1).startsWith(ccDigits))
  ));

  const phoneField = (waSuggested && !phoneEdit) ? (
    <div className="cf-phone-known">
      <div className="cf-phone-num" dir="ltr">{maskPhone(waSuggested)}</div>
      <div className="cf-phone-note">{t.phoneKnown}</div>
      <button className="cf-link" onClick={() => { setCcOpen(false); setPhoneEdit(true); }}>{t.changeNumber}</button>
    </div>
  ) : (
    <div className="cf-phone-edit">
      <div className="cf-phone-row">
        <button type="button" className="cf-input cf-cc" onClick={() => setCcOpen((o) => !o)}>
          <span>{flagOf(phoneIso)} <span dir="ltr">{phoneDial}</span></span>
          <span className="cf-arrow">▾</span>
        </button>
        <input
          className="cf-input cf-natnum" type="tel" inputMode="tel" dir="ltr"
          placeholder={t.phoneNatPh} value={phoneNat}
          onChange={(e) => setPhoneNat(e.target.value.replace(/[^\d\s-]/g, ''))}
        />
      </div>
      {ccOpen && (
        <div className="cf-cclist">
          <input
            className="cf-input cf-ccsearch" type="text" placeholder={t.countrySearchPh}
            value={ccQuery} onChange={(e) => setCcQuery(e.target.value)}
          />
          <div className="cf-ccscroll">
            {ccFiltered.map((c) => (
              <button
                type="button" key={c.iso} className="cf-ccitem"
                onClick={() => { setPhoneIso(c.iso); setCcOpen(false); setCcQuery(''); }}
              >
                <span className="cf-ccflag">{flagOf(c.iso)}</span>
                <span className="cf-ccname">{lang === 'zh' ? c.name_zh : c.name_en}</span>
                <span className="cf-ccdial" dir="ltr">{c.dial}</span>
              </button>
            ))}
            {!ccFiltered.length && <p className="cf-ccnone">{t.countryNone}</p>}
          </div>
        </div>
      )}
      {!!waSuggested && (
        <button className="cf-ghost" onClick={() => { setCcOpen(false); setPhoneEdit(false); }}>{t.useSuggested}</button>
      )}
    </div>
  );

  // ── Sign-in card (shared by desktop-right / mobile-inline) ────────────────
  const signinCard = (
    <div className="cf-signin" id="cf-signin">
      <div className="cf-si-shield">🛡</div>
      <h3 className="cf-si-title">{t.signinTitle}</h3>
      {(claimedOther && user) ? (
        // Already claimed and we're signed in as a DIFFERENT account — not necessarily
        // a thief: likely the owner on the wrong login, months later, unsure which
        // account they used. Two ways out and nothing about the other account: switch
        // accounts, or reach a human. Same pair the inbox resolver offers.
        <>
          <p className="cf-si-sub">{t.claimedInfo}</p>
          <button className="cf-link" onClick={async () => { await sb.auth.signOut(); window.location.reload(); }}>{t.claimedSignout}</button>
          {/* WhatsApp, not email: the claim link reached them there, so it is the
              channel they already have open. waLink() uses MAABAR_WHATSAPP, the
              canonical number — never a second hardcoded copy (docs/TECH_DEBT.md).
              textDecoration is neutralised inline because .cf-link was written for a
              <button> and sets none, so an <a> would underline next to one that does
              not. Cheaper than touching the stylesheet for one element. */}
          <a
            className="cf-link"
            style={{ textDecoration: 'none' }}
            href={waLink(t.waSupportText)}
            target="_blank"
            rel="noreferrer"
          >{t.claimedSupport}</a>
        </>
      ) : user ? (
        <>
          <p className="cf-si-sub">{t.signinSub}</p>
          <button className="cf-btn cf-primary" onClick={doClaim} disabled={busy}>{busy ? t.working : t.claim}</button>
        </>
      ) : (
        // Not signed in. When the factory is already claimed this is most likely the
        // OWNER re-opening the link logged out — let them sign in (claim_factory_by_slug
        // is a no-op for the owner, so they simply land back on their page). A non-owner
        // who signs in gets the RPC's "already claimed" error, which is correct.
        <>
          <p className="cf-si-sub">{claimedOther ? t.claimedInfo : t.signinSub}</p>
          {/* Method chooser — WhatsApp first (the claim link reached them there),
              email second. Both stay visible; WhatsApp says so when it is not live. */}
          {authMode === 'none' && (
            <>
              <button className="cf-btn cf-row" onClick={() => pickMethod('whatsapp')}>
                <span className="cf-ico"><WhatsAppIcon /> {t.whatsapp}</span>
                <span className="cf-arrow">›</span>
              </button>
              <button className="cf-btn cf-row" onClick={() => pickMethod('email')}>
                <span className="cf-ico"><EnvelopeIcon /> {t.email}</span>
                <span className="cf-arrow">›</span>
              </button>
            </>
          )}
          {/* Step one — identifier AND password together, one card, filled once. */}
          {authMode !== 'none' && !otpSent && (
            <div className="cf-form">
              {waBlocked && <p className="cf-msg">{t.waNotReady}</p>}
              {isWa
                ? phoneField
                : <input className="cf-input" type="email" inputMode="email" dir="ltr" placeholder={t.emailPh} value={email} onChange={(e) => setEmail(e.target.value)} />}
              <input className="cf-input" type="password" autoComplete="new-password" placeholder={t.newPassPh} value={pass} onChange={(e) => setPass(e.target.value)} />
              {waBlocked
                ? <button className="cf-btn cf-primary" onClick={() => pickMethod('email')}>{t.waUseEmail}</button>
                : <button className="cf-btn cf-primary" onClick={sendCode} disabled={busy}>{busy ? t.working : t.sendCode}</button>}
              {msg && <p className="cf-msg">{msg}</p>}
              <button className="cf-ghost" onClick={() => pickMethod('none')}>{t.back}</button>
            </div>
          )}
          {/* Step two — the code, in the slot the password field occupied. */}
          {authMode !== 'none' && otpSent && (
            <div className="cf-form">
              <p className="cf-si-sub">{t.codeSentTo(dest())}</p>
              <input className="cf-input" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={MAX_CODE} dir="ltr" placeholder={t.codePh} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, MAX_CODE))} />
              <button className="cf-btn cf-primary" onClick={verifyCode} disabled={busy || code.length < MIN_CODE}>{busy ? t.working : t.verifyBtn}</button>
              <button className="cf-ghost" onClick={sendCode} disabled={busy || cooldown > 0}>{cooldown > 0 ? t.resendIn(cooldown) : t.resend}</button>
              <button className="cf-ghost" onClick={() => { setOtpSent(false); setCode(''); setErr(''); }}>{t.changeMethod}</button>
            </div>
          )}
        </>
      )}
      {err && <p className="cf-err">{err}</p>}
      <p className="cf-si-security">🔒 {t.dataSecurity}</p>
    </div>
  );

  return shell(
    <>
      <div className="cf-grid">
        <div className="cf-left">
          <div className="cf-eyebrow">🛡 {t.eyebrow}</div>
          <h1 className="cf-title">{t.title} <span className="cf-spark">✦</span></h1>
          <p className="cf-subtitle">{t.subtitle}</p>

          <div className="cf-fcard">
            <div className="cf-flogo">{pre.profile_image ? <img src={pre.profile_image} alt="" loading="lazy" /> : <span>{name[0]}</span>}</div>
            <div className="cf-finfo">
              <div className="cf-fname">{name}{pre.is_verified && <span className="cf-vpill">✔ {t.verified}</span>}</div>
              {(nf(pre.city) || nf(pre.country)) && <div className="cf-fmeta">📍 {[nf(pre.city), nf(pre.country)].filter(Boolean).join(' · ')}</div>}
              {catLabel && <div className="cf-fmeta">🏭 {catLabel}</div>}
              <button className="cf-flink" onClick={() => nav(`/factory/${pre.factory_id}`)}>{t.previewPublic}</button>
            </div>
          </div>

          <div className="cf-stats">
            <div className="cf-stat"><div className="cf-stat-n">{total}</div><div className="cf-stat-l">{t.products}</div><div className="cf-stat-s">{t.productsSub}</div></div>
            <div className="cf-stat"><div className="cf-stat-n">{Number(pre.catalog_count) || 0}</div><div className="cf-stat-l">{t.catalogs}</div><div className="cf-stat-s">{t.catalogsSub}</div></div>
            {pre.pending_request_count > 0 && (
              <div className="cf-stat"><div className="cf-stat-n">{pre.pending_request_count}</div><div className="cf-stat-l">{t.inquiries}</div><div className="cf-stat-s">{t.inquiriesSub}</div></div>
            )}
          </div>

          <div className="cf-highlight"><span className="cf-hl-ic">★</span><p>{t.highlight}</p></div>
        </div>

        {signinCard}
      </div>

      {total > 0 && (
        <div className="cf-card cf-prod-section">
          <div className="cf-prod-head">
            <div>
              <h2 className="cf-sec-title">{t.productsTitle}</h2>
              <p className="cf-sec-sub">{t.productsReady}</p>
            </div>
            <button className="cf-pill-btn" onClick={() => nav(`/factory/${pre.factory_id}`)}>{t.viewAll(total)} ›</button>
          </div>
          <div className="cf-prod-grid">
            <div className="cf-prev-cards">
              {preview.slice(0, 4).map((p) => (
                <div className="cf-pcard" key={p.id}>
                  <div className="cf-pimg">{p.image && <img src={p.image} alt="" loading="lazy" />}</div>
                  <div className="cf-pbody">
                    <div className="cf-pname">{prodName(p)}</div>
                    <div className={`cf-pprice ${nf(p.price) ? 'on' : 'off'}`}>{nf(p.price) || t.priceTBD}</div>
                  </div>
                </div>
              ))}
              {total > Math.min(4, preview.length) && (
                <button className="cf-pmore" onClick={() => nav(`/factory/${pre.factory_id}`)}>{t.moreProducts(total - Math.min(4, preview.length))}</button>
              )}
            </div>

            <div className="cf-complete">
              <h3 className="cf-comp-title">{t.completeTitle}</h3>
              <div className="cf-ring">
                <svg viewBox="0 0 44 44" width="80" height="80">
                  <circle cx="22" cy="22" r="19" fill="none" stroke="#EDE7DD" strokeWidth="5" />
                  <circle cx="22" cy="22" r="19" fill="none" stroke="#2D6A4F" strokeWidth="5" strokeLinecap="round"
                    strokeDasharray={`${(pct / 100) * 119.4} 119.4`} transform="rotate(-90 22 22)" />
                </svg>
                <div className="cf-ring-n">{pct}%</div>
              </div>
              <div className="cf-comp-pct">{t.completePct}</div>
              <div className="cf-comp-line ok">✓ {t.pricedLine(priced)}</div>
              {unpriced > 0 && <div className="cf-comp-line warn">⏳ {t.unpricedLine(unpriced)}</div>}
              <div className="cf-comp-hint">ℹ {t.completeHint}</div>
              <button className="cf-btn cf-primary" onClick={goSignin}>{t.updateBtn} ›</button>
            </div>
          </div>
        </div>
      )}

      <div className="cf-card cf-how">
        <h2 className="cf-how-title">{t.howTitle}</h2>
        <div className="cf-steps">
          {[['1', t.step1t, t.step1s, '🔍'], ['2', t.step2t, t.step2s, '✦'], ['3', t.step3t, t.step3s, '💬']].map(([n, tt, ss, ic]) => (
            <div className="cf-step" key={n}>
              <div className="cf-step-ic">{ic}</div>
              <div className="cf-step-body"><div className="cf-step-t"><span className="cf-step-n">{n}</span> {tt}</div><div className="cf-step-s">{ss}</div></div>
            </div>
          ))}
        </div>
      </div>

      <div className="cf-trust">
        {[t.trust1, t.trust2, t.trust3, t.trust4].map((x, i) => (
          <div className={`cf-trust-item${i === 0 ? ' hot' : ''}`} key={i}><span className="cf-trust-ic">{i === 0 ? '✓' : '◆'}</span>{x}</div>
        ))}
      </div>
    </>
  );
}

const CSS = `
.cf-root { min-height:100dvh; background:#FAF8F5; color:#1A1814; max-width:1040px; margin:0 auto; padding:20px 18px 44px; font-family:var(--font-sans),system-ui,sans-serif; }
.cf-center { text-align:center; padding:60px 0; color:#6E6A62; }

.cf-top { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:18px; }
.cf-brand { display:flex; align-items:center; gap:11px; }
.cf-mark { width:38px; height:38px; border-radius:11px; background:#1A1814; color:#fff; display:grid; place-items:center; font-weight:800; font-size:19px; }
.cf-wordmark { font-weight:700; letter-spacing:.18em; font-size:15px; }
.cf-wordmark span { font-family:var(--font-ar); letter-spacing:0; color:#8a8578; margin-inline-start:5px; }
.cf-brandsub { font-size:11px; color:#9C978D; margin-top:1px; }
.cf-langs { display:flex; gap:4px; flex-shrink:0; }
.cf-langs button { background:#fff; border:1px solid #ECE7DF; border-radius:99px; padding:5px 12px; font-size:12px; font-weight:600; color:#6E6A62; cursor:pointer; }
.cf-langs button.on { background:#1A1814; color:#fff; border-color:#1A1814; }

/* hero grid */
.cf-grid { display:grid; grid-template-columns:1fr; gap:18px; }
.cf-eyebrow { display:inline-flex; align-items:center; gap:7px; font-size:12px; font-weight:700; color:#8B7355; background:rgba(139,115,85,.10); border:1px solid rgba(139,115,85,.18); border-radius:99px; padding:5px 13px; }
.cf-title { font-size:30px; line-height:1.18; font-weight:800; letter-spacing:-.01em; margin:14px 0 0; }
.cf-spark { color:#A6864F; }
.cf-subtitle { font-size:14px; color:#6E6A62; line-height:1.7; margin:10px 0 0; max-width:52ch; }

.cf-fcard { display:flex; gap:14px; align-items:flex-start; background:#fff; border:1px solid #ECE7DF; border-radius:16px; padding:16px; margin-top:20px; box-shadow:0 6px 20px rgba(26,24,20,.04); }
.cf-flogo { width:64px; height:64px; border-radius:14px; background:#F0EBE3; border:1px solid #E6DFD3; overflow:hidden; flex-shrink:0; display:grid; place-items:center; color:#8B7355; font-weight:800; font-size:26px; }
.cf-flogo img { width:100%; height:100%; object-fit:cover; }
.cf-finfo { min-width:0; }
.cf-fname { font-size:19px; font-weight:800; display:flex; align-items:center; gap:9px; flex-wrap:wrap; }
.cf-vpill { font-size:11px; font-weight:700; color:#2D6A4F; background:rgba(45,106,79,.09); border:1px solid rgba(45,106,79,.22); border-radius:99px; padding:2px 9px; }
.cf-fmeta { font-size:13px; color:#6E6A62; margin-top:4px; }
.cf-flink { background:none; border:none; color:#8B7355; font-weight:700; font-size:12.5px; cursor:pointer; padding:6px 0 0; }

.cf-stats { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; margin-top:14px; }
.cf-stat { background:#fff; border:1px solid #ECE7DF; border-radius:14px; padding:14px 10px; text-align:center; box-shadow:0 4px 14px rgba(26,24,20,.03); }
.cf-stat-n { font-size:24px; font-weight:800; }
.cf-stat-l { font-size:12px; font-weight:700; margin-top:1px; }
.cf-stat-s { font-size:10.5px; color:#9C978D; margin-top:2px; }

.cf-highlight { display:flex; gap:12px; align-items:center; background:linear-gradient(135deg,#FCF7EE,#F4F8F2); border:1px solid rgba(166,134,79,.28); border-radius:16px; padding:15px 17px; margin-top:16px; }
.cf-hl-ic { flex-shrink:0; width:34px; height:34px; border-radius:10px; background:rgba(166,134,79,.14); color:#A6864F; display:grid; place-items:center; font-size:16px; }
.cf-highlight p { margin:0; font-size:13px; line-height:1.6; font-weight:600; color:#5a4e3a; }

/* sign-in card */
.cf-signin { background:#fff; border:1px solid #ECE7DF; border-radius:20px; padding:24px 22px; box-shadow:0 12px 34px rgba(26,24,20,.07); align-self:start; }
.cf-si-shield { width:52px; height:52px; border-radius:15px; background:rgba(139,115,85,.10); color:#8B7355; display:grid; place-items:center; font-size:24px; margin:0 auto 12px; }
.cf-si-title { text-align:center; font-size:18px; font-weight:800; margin:0; }
.cf-si-sub { text-align:center; font-size:12.5px; color:#9C978D; margin:6px 0 16px; line-height:1.6; }
.cf-si-security { text-align:center; font-size:11px; color:#9C978D; line-height:1.6; margin:16px 0 0; }

.cf-btn { width:100%; display:flex; align-items:center; justify-content:center; gap:9px; background:#fff; border:1px solid #DED8CE; border-radius:13px; padding:13px 15px; font-size:14px; font-weight:600; color:#1A1814; cursor:pointer; margin-top:9px; font-family:inherit; transition:border-color .12s, background .12s; }
.cf-btn:hover { border-color:#c9c1b2; }
.cf-btn:disabled { opacity:.55; cursor:default; }
.cf-btn.cf-row { justify-content:space-between; }
/* The label and its icon travel together; the arrow stays pushed to the end by
   .cf-row. flex-shrink guards the glyph when a long zh label wraps. */
.cf-ico { display:inline-flex; align-items:center; gap:9px; text-align:start; }
.cf-ico svg { flex-shrink:0; }
.cf-btn.cf-primary { background:#1A1814; color:#fff; border-color:#1A1814; justify-content:center; }
.cf-btn.cf-primary:hover { opacity:.9; }
.cf-g { width:20px; height:20px; border-radius:50%; background:#fff; border:1px solid #E6DFD3; color:#4285F4; font-weight:800; display:grid; place-items:center; font-size:12px; }
.cf-arrow { color:#B9B2A6; font-size:18px; }
.cf-or { display:flex; align-items:center; text-align:center; color:#B9B2A6; font-size:11px; margin:14px 0 4px; }
.cf-or::before, .cf-or::after { content:''; flex:1; height:1px; background:#ECE7DF; }
.cf-or span { padding:0 12px; }
.cf-link { display:block; width:100%; text-align:center; background:none; border:none; color:#8B7355; font-weight:700; font-size:13px; cursor:pointer; padding:8px 0; }
.cf-ghost { width:100%; background:none; border:none; color:#9C978D; font-size:13px; cursor:pointer; padding:9px; margin-top:4px; }
.cf-input { width:100%; box-sizing:border-box; border:1px solid #DED8CE; border-radius:12px; padding:12px 14px; font-size:15px; margin-top:9px; outline:none; background:#fff; font-family:inherit; }
.cf-input:focus { border-color:#8B7355; }
.cf-form { text-align:center; }
.cf-claimed { font-size:13px; color:#c0503f; text-align:center; line-height:1.6; }
.cf-err { color:#c0503f; font-size:12.5px; text-align:center; margin:12px 0 0; }
.cf-msg { color:#2D6A4F; font-size:12.5px; text-align:center; margin:10px 0 0; }

/* products section */
.cf-card { background:#fff; border:1px solid #ECE7DF; border-radius:20px; padding:22px; margin-top:20px; box-shadow:0 6px 20px rgba(26,24,20,.04); }
.cf-prod-head { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:16px; flex-wrap:wrap; }
.cf-sec-title { font-size:19px; font-weight:800; margin:0; }
.cf-sec-sub { font-size:12.5px; color:#9C978D; margin:4px 0 0; }
.cf-pill-btn { background:#F4F1EA; border:1px solid #E6DFD3; border-radius:99px; padding:8px 15px; font-size:12.5px; font-weight:700; color:#5a4e3a; cursor:pointer; white-space:nowrap; }
.cf-prod-grid { display:grid; grid-template-columns:1fr; gap:16px; }
.cf-prev-cards { display:grid; grid-template-columns:repeat(2,1fr); gap:10px; }
.cf-pcard { border:1px solid #EFE9DE; border-radius:13px; overflow:hidden; background:#fff; }
.cf-pimg { aspect-ratio:1/1; background:#F0EBE3 center/cover no-repeat; }
.cf-pimg img { width:100%; height:100%; object-fit:cover; display:block; }
.cf-pbody { padding:9px 11px 11px; }
.cf-pname { font-size:12.5px; font-weight:600; line-height:1.4; height:2.7em; overflow:hidden; }
.cf-pprice { font-size:13px; font-weight:800; margin-top:5px; }
.cf-pprice.on { color:#1A1814; }
.cf-pprice.off { color:#A6864F; font-weight:700; font-size:12px; }
.cf-pmore { border:1px dashed #DED8CE; border-radius:13px; background:#FAF8F5; color:#8B7355; font-weight:700; font-size:13px; cursor:pointer; display:grid; place-items:center; min-height:90px; }

.cf-complete { border:1px solid #EFE9DE; border-radius:16px; padding:18px; text-align:center; background:#FCFAF6; }
.cf-comp-title { font-size:14px; font-weight:800; margin:0 0 12px; }
.cf-ring { position:relative; width:80px; height:80px; margin:0 auto; }
.cf-ring-n { position:absolute; inset:0; display:grid; place-items:center; font-size:19px; font-weight:800; color:#2D6A4F; }
.cf-comp-pct { font-size:11.5px; color:#9C978D; margin:6px 0 12px; }
.cf-comp-line { font-size:12.5px; font-weight:600; margin-top:6px; }
.cf-comp-line.ok { color:#2D6A4F; }
.cf-comp-line.warn { color:#A6864F; }
.cf-comp-hint { font-size:11.5px; color:#9C978D; margin:12px 0 14px; }

/* how it works */
.cf-how-title { font-size:19px; font-weight:800; margin:0 0 18px; text-align:center; }
.cf-steps { display:grid; grid-template-columns:1fr; gap:14px; }
.cf-step { display:flex; gap:13px; align-items:flex-start; }
.cf-step-ic { width:46px; height:46px; border-radius:13px; background:rgba(139,115,85,.09); display:grid; place-items:center; font-size:20px; flex-shrink:0; }
.cf-step-t { font-size:14px; font-weight:800; }
.cf-step-n { display:inline-grid; place-items:center; width:18px; height:18px; border-radius:50%; background:#8B7355; color:#fff; font-size:11px; font-weight:800; vertical-align:middle; }
.cf-step-s { font-size:12.5px; color:#6E6A62; margin-top:3px; line-height:1.6; }

/* trust strip */
.cf-trust { display:grid; grid-template-columns:repeat(2,1fr); gap:10px; margin-top:20px; }
.cf-trust-item { display:flex; align-items:center; gap:9px; background:#fff; border:1px solid #ECE7DF; border-radius:13px; padding:13px 14px; font-size:12.5px; font-weight:700; color:#5a4e3a; }
.cf-trust-item.hot { background:rgba(45,106,79,.06); border-color:rgba(45,106,79,.22); color:#2D6A4F; }
.cf-trust-ic { color:#8B7355; }
.cf-trust-item.hot .cf-trust-ic { color:#2D6A4F; }

.cf-foot { text-align:center; font-size:11px; color:#B9B2A6; margin:30px 0 0; }

/* post-claim pricing step */
.cf-hero-head { text-align:center; margin-top:8px; }
.cf-welcome { font-size:15px; font-weight:700; color:#6E6A62; margin:0 0 6px; }
.cf-price-card { background:#fff; border:1px solid #ECE7DF; border-radius:16px; padding:18px; margin:18px 0; text-align:center; }
.cf-price-hint { font-size:14px; font-weight:700; margin-bottom:12px; }
.cf-price-row { display:flex; gap:8px; }
.cf-cur { max-width:96px; }
.cf-prodlist { display:flex; flex-direction:column; gap:9px; }
.cf-prod { display:flex; gap:12px; background:#fff; border:1px solid #ECE7DF; border-radius:13px; padding:10px; align-items:center; }
.cf-prod-img { width:52px; height:52px; border-radius:10px; background:#F0EBE3 center/cover no-repeat; flex-shrink:0; }
.cf-prod-name { font-size:13px; font-weight:600; }
.cf-prod-price { font-size:12px; font-weight:700; margin-top:2px; }
.cf-prod-price.on { color:#2D6A4F; }
.cf-prod-price.off { color:#A6864F; }

/* desktop */
@media (min-width:880px) {
  .cf-root { padding:26px 26px 56px; }
  .cf-grid { grid-template-columns:1.55fr 1fr; align-items:start; gap:26px; }
  .cf-title { font-size:36px; }
  .cf-prod-grid { grid-template-columns:1.6fr 1fr; }
  .cf-prev-cards { grid-template-columns:repeat(2,1fr); }
  .cf-steps { grid-template-columns:repeat(3,1fr); }
  .cf-trust { grid-template-columns:repeat(4,1fr); }
}

/* WhatsApp number: the masked suggestion, and the country + number editor */
.cf-phone-known { border:1px solid #ECE7DF; border-radius:12px; background:#FAF8F5; padding:12px 14px 8px; margin-top:9px; }
.cf-phone-num { font-size:17px; font-weight:800; letter-spacing:.4px; }
.cf-phone-note { font-size:11.5px; color:#9C978D; margin-top:3px; }
.cf-phone-known .cf-link { font-size:12.5px; padding:6px 0 0; }
.cf-phone-row { display:flex; gap:8px; }
.cf-cc { width:auto; flex:0 0 116px; display:flex; align-items:center; justify-content:space-between; gap:6px; font-size:14px; text-align:start; cursor:pointer; }
.cf-natnum { flex:1; min-width:0; }
.cf-cclist { border:1px solid #DED8CE; border-radius:12px; background:#fff; margin-top:8px; overflow:hidden; }
.cf-ccsearch { width:calc(100% - 16px); margin:8px; }
.cf-ccscroll { max-height:210px; overflow-y:auto; }
.cf-ccitem { width:100%; display:flex; align-items:center; gap:9px; background:none; border:none; border-top:1px solid #F2EDE5; padding:9px 12px; font-size:13px; font-family:inherit; color:#1A1814; text-align:start; cursor:pointer; }
.cf-ccitem:hover { background:#FAF8F5; }
.cf-ccflag { font-size:16px; }
.cf-ccname { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.cf-ccdial { color:#9C978D; font-weight:700; }
.cf-ccnone { font-size:12.5px; color:#9C978D; padding:12px; margin:0; }
`;
